import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

const PP = "'Poppins', sans-serif";

// One mermaid instance for the app, loaded the first time a chart is drawn so the
// library never costs anything on pages that have no charts.
let mermaidPromise = null;
function getMermaid(dark) {
    if (!mermaidPromise) {
        mermaidPromise = import('mermaid').then(m => {
            const mermaid = m.default;
            mermaid.initialize({
                startOnLoad: false,
                securityLevel: 'strict',
                theme: dark ? 'dark' : 'default',
                // The document's own stack and spacing, copied from its mermaid.initialize.
                // Poppins at a wider rank spacing made every box taller and the labels read
                // larger than the charts were drawn for. (user 2026-10-04: "the font all
                // please use back as it is. font size of this flow charts")
                fontFamily: '"Source Sans 3",system-ui,"PingFang SC","Microsoft YaHei",sans-serif',
                flowchart: { curve: 'basis', nodeSpacing: 22, rankSpacing: 30, padding: 8, htmlLabels: true },
            });
            return mermaid;
        });
    }
    return mermaidPromise;
}

// The company colours, appended to every chart so the classes in the source resolve.
const CLASS_DEFS = `
  classDef nx fill:#e2733b,stroke:#e2733b,color:#fff,font-weight:600
  classDef as fill:#2f8fd8,stroke:#2f8fd8,color:#fff,font-weight:600
  classDef ask fill:#ffffff,stroke:#5b6475,color:#1d2330
  classDef go fill:none,stroke:#5b6475,stroke-dasharray:4 3,color:#1d2330,font-weight:700`;

let seq = 0;

/**
 * One SOP flowchart.
 *
 * Rendered in the app rather than inside an embedded document, so a tap on a box
 * is an ordinary React event: the page decides what it means, and a "→ Step" box
 * can route through the app's own router instead of changing an iframe's hash.
 *
 * `highlightId` is the node the walkthrough is on — it is lit and everything else
 * is dimmed, which is what makes the spoken walkthrough followable.
 */
export default function SopChart({ source, highlightId, selectedId, onNodeClick, isOff }) {
    const holderRef = useRef(null);
    const [svg, setSvg] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let gone = false;
        setLoading(true);
        setError('');
        getMermaid(false)
            .then(mermaid => mermaid.render('sopchart' + (seq++), (source || '') + CLASS_DEFS))
            .then(({ svg: out }) => { if (!gone) { setSvg(out); setLoading(false); } })
            .catch(() => {
                if (gone) return;
                setError('This chart could not be drawn.');
                setLoading(false);
            });
        return () => { gone = true; };
    }, [source]);

    // Mermaid writes node ids as "flowchart-<id>-<n>"; recover the author's id so the
    // walkthrough and the click handler can talk about boxes by the names in the source.
    const idOf = (el) => {
        const m = (el.id || '').match(/flowchart-(.+?)-\d+$/);
        return m ? m[1] : null;
    };

    useEffect(() => {
        const root = holderRef.current;
        if (!root || !svg) return;
        const nodes = Array.from(root.querySelectorAll('.node'));
        const anyHighlight = Boolean(highlightId);

        nodes.forEach(n => {
            const id = idOf(n);
            const lit = anyHighlight && id === highlightId;
            const picked = !anyHighlight && id === selectedId;
            // Not this role's work: faded right back, under even the walkthrough's dim,
            // which is the document's own treatment (.node.off { opacity: .16 }).
            const off = isOff ? isOff(id) : false;

            n.style.cursor = 'pointer';
            n.style.transition = 'opacity .25s';
            n.style.opacity = off ? '0.16' : (anyHighlight && !lit ? '0.3' : '1');

            n.querySelectorAll('rect, polygon, path, circle').forEach(shape => {
                if (lit || picked) {
                    shape.style.stroke = '#F5A623';
                    shape.style.strokeWidth = lit ? '5px' : '4px';
                } else {
                    shape.style.stroke = '';
                    shape.style.strokeWidth = '';
                }
            });
            n.style.filter = lit ? 'drop-shadow(0 0 10px rgba(245,166,35,.85))' : '';
        });

        const onClick = (e) => {
            const node = e.target.closest?.('.node');
            if (!node) return;
            const id = idOf(node);
            if (id) onNodeClick?.(id);
        };
        root.addEventListener('click', onClick);
        return () => root.removeEventListener('click', onClick);
    }, [svg, highlightId, selectedId, onNodeClick, isOff]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress size={26} />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography sx={{ fontFamily: PP, fontSize: '0.84rem', color: '#DC2626', py: 3, textAlign: 'center' }}>
                {error}
            </Typography>
        );
    }

    return (
        <Box
            ref={holderRef}
            dangerouslySetInnerHTML={{ __html: svg }}
            sx={{
                // The SVG is produced by mermaid from our own committed flowchart source —
                // no user input reaches it, which is why this one place may set HTML.
                overflowX: 'auto',
                textAlign: 'center',
                // Mermaid's own inline max-width is the chart's natural size; it is left
                // alone, so the chart shrinks to fit a phone and is never blown up past
                // its own size on a wide screen, where every label would be enormous.
                '& svg': { height: 'auto' },
                // No font override here either - mermaid's own sizing is what the charts
                // were laid out against. Only the italic rule the document itself carries.
                '& .nodeLabel i': { fontWeight: 400, opacity: 0.85 },
            }}
        />
    );
}
